import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search } from 'lucide-react';

function CustomSearchInput({
  label,
  name,
  value,
  onChange,
  placeholder,
  suggestions = [],
  disabled = false,
  maxSuggestions = 8,
  hasError = false,
  showSuggestionsOnFocus = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const listboxId = `${name}-suggestions`;
  const filteredSuggestions = useMemo(() => {
    const normalizedValue = normalizeSuggestionSearchText(value);
    const matchingSuggestions = suggestions.filter((suggestion) =>
      [
        suggestion.value,
        suggestion.label,
        suggestion.searchValue,
      ]
        .filter(Boolean)
        .some((suggestionValue) =>
          normalizeSuggestionSearchText(suggestionValue).includes(normalizedValue)
        )
    );

    return matchingSuggestions.slice(0, maxSuggestions);
  }, [maxSuggestions, suggestions, value]);
  const hasSearchValue = String(value || '').trim().length > 0;
  const shouldShowEmptySuggestions = showSuggestionsOnFocus && !hasSearchValue;
  const hasSuggestions = (hasSearchValue || shouldShowEmptySuggestions) && filteredSuggestions.length > 0;

  useEffect(() => {
    function handlePointerDown(event) {
      if (!searchRef.current?.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  function handleChange(event) {
    onChange(event);
    setIsOpen(true);
    setActiveIndex(-1);
  }

  function handleFocus() {
    if (hasSearchValue || showSuggestionsOnFocus) {
      setIsOpen(true);
    }
  }

  function focusInput() {
    inputRef.current?.focus();
  }

  function handleKeyDown(event) {
    if (!hasSearchValue && !showSuggestionsOnFocus) {
      return;
    }

    if (!isOpen && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      setIsOpen(true);
      return;
    }

    if (!hasSuggestions) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((currentIndex) =>
        currentIndex >= filteredSuggestions.length - 1 ? 0 : currentIndex + 1
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((currentIndex) =>
        currentIndex <= 0 ? filteredSuggestions.length - 1 : currentIndex - 1
      );
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      handleSelect(filteredSuggestions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }

  function handleSelect(suggestion) {
    onChange({
      target: {
        name,
        value: String(suggestion.value || suggestion.label || ''),
      },
      suggestion,
    });
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }

  return (
    <div className="relative grid min-w-0 gap-1.5" ref={searchRef}>
      <label className="text-[0.74rem] font-medium text-[var(--theme-text-muted)]" htmlFor={name}>
        {label}
      </label>
      <div
        className={`flex min-h-[40px] w-full min-w-0 cursor-text items-center gap-2 rounded-full border bg-white px-3 focus-within:ring-2 ${
          hasError
            ? 'border-[#df6d5f] focus-within:border-[#c24738] focus-within:ring-[#f7d5cf]'
            : 'border-[var(--theme-border)] focus-within:border-[var(--theme-accent)] focus-within:ring-[var(--theme-accent-soft)]'
        }`}
        onMouseDown={(event) => {
          if (event.target !== inputRef.current) {
            event.preventDefault();
            focusInput();
          }
        }}
      >
        <Search
          className="pointer-events-none h-4 w-4 shrink-0 text-[var(--theme-text-muted)]"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          className="h-[38px] w-full min-w-0 appearance-none border-0 bg-transparent p-0 text-[0.84rem] caret-[var(--theme-accent)] outline-none placeholder:text-[var(--theme-text-muted)]"
          type="text"
          inputMode="search"
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen && hasSuggestions}
          aria-invalid={hasError}
        />
      </div>

      {isOpen && hasSuggestions ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-[16px] border border-[var(--theme-border)] bg-white p-1.5 shadow-[0_18px_45px_rgba(var(--theme-shadow-rgb),0.16)]">
          <div id={listboxId} className="max-h-52 overflow-y-auto" role="listbox">
            {filteredSuggestions.map((suggestion, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  key={`${suggestion.group || 'suggestion'}-${suggestion.value}-${index}`}
                  className={`grid min-h-[36px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 rounded-[12px] px-2.5 py-1.5 text-left transition ${
                    isActive ? 'bg-[var(--theme-accent-soft)]' : 'hover:bg-[var(--theme-surface-soft)]'
                  }`}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(suggestion)}
                  role="option"
                  aria-selected={isActive}
                >
                  <span className="min-w-0 truncate text-[0.82rem] font-medium text-[var(--theme-text-strong)]">
                    {suggestion.label || suggestion.value}
                  </span>
                  {suggestion.status && suggestion.group === 'po-code' ? (
                    <span
                      className={`row-span-2 self-center shrink-0 rounded-full px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.08em] ${getSuggestionStatusClassName(
                        suggestion.status
                      )}`}
                    >
                      {suggestion.status}
                    </span>
                  ) : null}
                  {suggestion.detail ? (
                    <span
                      className={`truncate text-[0.7rem] text-[var(--theme-text-muted)] ${
                        suggestion.actionLabel ? 'col-start-1' : ''
                      }`}
                    >
                      {suggestion.detail}
                    </span>
                  ) : null}
                  {suggestion.actionLabel ? (
                    <span className="col-start-2 row-start-1 row-span-2 inline-flex shrink-0 items-center gap-1 self-center rounded-full bg-[var(--theme-accent-soft)] px-2 py-1 text-[0.68rem] font-semibold text-[var(--theme-accent)]">
                      <Plus className="h-3 w-3" aria-hidden="true" />
                      {suggestion.actionLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function normalizeSuggestionSearchText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[₱,\s]/g, '');
}

function getSuggestionStatusClassName(status) {
  if (status === 'Completed') {
    return 'bg-[#ecf7ef] text-[#2f7a43]';
  }

  if (status === 'Received') {
    return 'bg-[#eef4fb] text-[#3f668f]';
  }

  if (status === 'Cancelled') {
    return 'bg-[#fff1ed] text-[#bf5d4b]';
  }

  if (status === 'In Progress') {
    return 'bg-[#fff5df] text-[#9a6a12]';
  }

  return 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]';
}

export default CustomSearchInput;
